import { useState, useEffect } from "react";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import HStack from "@ui-kit/layout/Stack/HStack.web";
import VStack from "@ui-kit/layout/Stack/VStack.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import styled from "@emotion/styled";
import { domElementIds } from "@shared/constants/common";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import { useTranslation } from "next-i18next";
import Link from "next/link";

import { formatTimer, useTimer } from "@ui-kit/feedback/timer/useTimer";
import moment from "moment-timezone";

const { headerDetailsId } = domElementIds;

const StyledFlex = styled(Flex)`
  background-color: var(--chakra-colors-bg);
  width: 100%;
`;

const StyledBox = styled(Box)`
  margin-inline-start: 0px !important;
  flex-direction: column;
`;

const StyledVStack = styled(VStack)`
  margin-inline-start: 0px !important;
  flex-direction: column;
`;

export interface HeaderDetailsProps {
  title: string;
  subtitle?: string;
  organizer?: NestedPublicVenueSerializer;
  saleStartsOn?: string;
  timezone?: string;
  statusElement?: JSX.Element;
  imageElement: JSX.Element;
  badgeElement: JSX.Element;
  isSmallerThanSm: boolean;
  isTablet: boolean;
  // Whether the component is in a small container, such as a modal or a card
  isModal?: boolean;
  openContactDetailsModal?: () => void;
  isLoading: boolean;
  fontSize?: "small" | "regular";
  columnGap?: string;
}

const HeaderDetails = ({
  saleStartsOn = undefined,
  title = "",
  subtitle = "",
  organizer = undefined,
  statusElement = <></>,
  imageElement = <></>,
  badgeElement = <></>,
  openContactDetailsModal = () => {
    /* empty */
  },
  isSmallerThanSm = false,
  isTablet = false,
  isLoading = false,
  timezone = "",
  isModal = false,
  fontSize = "regular",
  columnGap = "element.3xl",
}: HeaderDetailsProps): JSX.Element => {
  const skeletonText = "skeletonText";

  const { t } = useTranslation("common");

  const orgClicker = (e) => {
    e.preventDefault();
    openContactDetailsModal();
  };

  const orgUrl = organizer ? `/o/${organizer.slug}/` : null;

  const saleStartsOnTz = moment.tz(saleStartsOn, timezone).toDate();
  const [timeRemaining] = useTimer(saleStartsOnTz);
  const { hours, minutes, seconds } = formatTimer(timeRemaining);

  // Hydration-safe date comparison - server renders as true to avoid mismatch
  const [saleStartsOnPassed, setSaleStartsOnPassed] = useState(true);
  useEffect(() => {
    if (saleStartsOn) {
      setSaleStartsOnPassed(Date.now() >= new Date(saleStartsOn).valueOf());
    }
  }, [saleStartsOn]);

  const showSaleStartsOn = saleStartsOn && !saleStartsOnPassed;

  const saleStartsOnComponent = (
    <Flex
      w="100%"
      flexDirection="column"
      data-testid="event-details-header-sale-starts-on"
    >
      <Text
        as="h3"
        fontSize="xl"
        fontWeight="bold"
        width="100%"
        color="purple.fg"
        mb="xs"
      >
        {t("Sale starts in")}
      </Text>
      <Flex direction="row" width="100%">
        <Box
          width="33%"
          padding="xs"
          textAlign="center"
          bg="purple.subtle"
          borderRadius="md"
          marginBottom="lg"
        >
          <Text fontWeight="bold" fontSize="4xl" color="purple.fg">
            {timeRemaining > 0 ? hours : "0"}
          </Text>
          <Text color="purple.fg">{t("Hour", { count: Number(hours) })}</Text>
        </Box>
        <Box
          width="33%"
          padding="xs"
          textAlign="center"
          bg="purple.subtle"
          borderRadius="md"
          marginLeft="xs"
          marginRight="xs"
          marginBottom="lg"
        >
          <Text fontWeight="bold" fontSize="4xl" color="purple.fg">
            {timeRemaining > 0 ? minutes : "0"}
          </Text>
          <Text color="purple.fg">
            {t("Minute", { count: Number(minutes) })}
          </Text>
        </Box>
        <Box
          width="33%"
          padding="xs"
          textAlign="center"
          bg="purple.subtle"
          borderRadius="md"
          marginBottom="lg"
        >
          <Text fontWeight="bold" fontSize="4xl" color="purple.fg">
            {timeRemaining > 0 ? seconds : "0"}
          </Text>
          <Text color="purple.fg">
            {t("Second", { count: Number(seconds) })}
          </Text>
        </Box>
      </Flex>
    </Flex>
  );

  const mobileComponent: JSX.Element = (
    <Box id={headerDetailsId} data-testid="event-details-header-mobile" bg="bg">
      <Skeleton isLoaded={!isLoading}>
        <Box data-testid="event-image">{imageElement}</Box>
      </Skeleton>
      <Box minWidth={columnGap} />
      <Flex padding="16px 20px" direction="column">
        {showSaleStartsOn ? saleStartsOnComponent : null}
        <Skeleton width="100%" isLoaded={!isLoading}>
          <Text
            as="h1"
            fontSize="lg"
            fontWeight="bold"
            color="fg"
            data-testid="title"
          >
            {title || skeletonText}
          </Text>
        </Skeleton>
        <Text as="h2" fontSize="md" color="fg.muted" data-testid="subtitle">
          {subtitle}
        </Text>
        {organizer && (
          <Text as="h3" fontSize="sm" color="fg.muted" data-testid="organizer">
            <Link href={orgUrl} onClick={orgClicker}>
              {organizer.name}
            </Link>
          </Text>
        )}
        {statusElement}
        <Skeleton isLoaded={!isLoading}>{badgeElement}</Skeleton>
      </Flex>
    </Box>
  );

  const tabletComponent: JSX.Element = (
    <Flex
      padding="24px 0px"
      data-testid="tablet-header-details"
      id={headerDetailsId}
      bg="bg"
    >
      <Skeleton borderRadius="md" isLoaded={!isLoading}>
        <Box data-testid="event-image">{imageElement}</Box>
      </Skeleton>
      <Box minWidth={columnGap} />
      <Flex direction="column" alignItems="flex-start">
        {showSaleStartsOn ? saleStartsOnComponent : null}
        <Skeleton
          width="100%"
          isLoaded={!isLoading}
          {...(isLoading && {
            mb: "8px",
          })}
        >
          <Text
            as="h1"
            fontSize={fontSize === "small" ? "lg" : "3xl"}
            fontWeight="bold"
            color="fg"
            data-testid="title"
          >
            {title || skeletonText}
          </Text>
        </Skeleton>
        <Text
          as="h2"
          fontSize={fontSize === "small" ? "sm" : "lg"}
          color="fg.muted"
          data-testid="subtitle"
        >
          {subtitle}
        </Text>
        {organizer && (
          <Text
            as="h3"
            fontSize={fontSize === "small" ? "sm" : "md"}
            color="fg.muted"
            data-testid="organizer"
          >
            <Link href={orgUrl} onClick={orgClicker}>
              {organizer.name}
            </Link>
          </Text>
        )}
        {statusElement}
        <Skeleton isLoaded={!isLoading}>{badgeElement}</Skeleton>
      </Flex>
    </Flex>
  );

  const desktopComponent: JSX.Element = (
    <Flex
      padding="24px 0px"
      id={headerDetailsId}
      data-testid="event-details-header-desktop"
      bg="bg"
    >
      <HStack width="full" alignItems="flex-start" alignContent="flex-start">
        <StyledVStack minWidth="60%" maxWidth="60%">
          <Skeleton
            width="100%"
            height="100%"
            borderRadius="md"
            isLoaded={!isLoading}
          >
            <Box data-testid="event-image">{imageElement}</Box>
          </Skeleton>
        </StyledVStack>
        <StyledBox minWidth={columnGap} />
        <StyledVStack width="40%" alignItems="flex-start" data-testid="details">
          {showSaleStartsOn ? saleStartsOnComponent : null}
          <Flex direction="column">
            <Skeleton width="100%" isLoaded={!isLoading}>
              <Text
                as="h1"
                fontSize={fontSize === "small" ? "lg" : "3xl"}
                fontWeight="bold"
                color="fg"
                data-testid="title"
              >
                {title || skeletonText}
              </Text>
            </Skeleton>
            {subtitle && (
              <Text
                as="h2"
                fontSize={fontSize === "small" ? "sm" : "lg"}
                color="fg.muted"
                pb="xs"
                data-testid="subtitle"
              >
                {subtitle}
              </Text>
            )}
            {organizer && (
              <Text
                as="h3"
                fontSize={fontSize === "small" ? "sm" : "md"}
                color="fg.muted"
                data-testid="organizer"
              >
                <Link href={orgUrl} onClick={orgClicker}>
                  {organizer.name}
                </Link>
              </Text>
            )}
            {statusElement}
            <Skeleton isLoaded={!isLoading}>{badgeElement}</Skeleton>
          </Flex>
        </StyledVStack>
      </HStack>
    </Flex>
  );
  const getComponent = () => {
    if (isSmallerThanSm) {
      return mobileComponent;
    }

    if (isTablet || isModal) {
      return tabletComponent;
    }

    return desktopComponent;
  };

  return <StyledFlex direction="column">{getComponent()}</StyledFlex>;
};

export default HeaderDetails;
